import EventProvider from "sap/ui/base/EventProvider";
import User from "./User";
import Event from "sap/ui/base/Event";
/**
 * Represents a menu configuration and provides methods to manage it.
 * @namespace sap.mcsuite.model.ts
 */
export default class Menu extends EventProvider {
	private menuConfig: { menu: Array<MenuItem> };
	private static instance: Menu;
    private initialized: boolean;
    private static readonly MENU_CONFIG_URL = 'sap/mcsuite/model/menu_config.json';

	private static readonly metadata = {
		'events': {
			'menuChange': {}
		}
	};

    private constructor() {
		super();
		this.menuConfig = {menu: []};
		this.initialized = false;
	}

    public static getInstance(): Menu {
        if (!Menu.instance) {
            Menu.instance = new Menu();
        }
        return Menu.instance;
    }

    public isInitialized(): boolean {
        return this.initialized
    }

    public async initialize(user: User): Promise<void> {
        if (!this.initialized) {
            await this.loadMenuConfig(user);
            this.initialized = true;
			// Fire the menuChange event after loading the menu configuration
			this.fireMenuChange();
        }
    }

	/**
	 * Loads the menu configuration from a JSON file and filters it based on the user's roles.
	 *
	 * @param user - The user object containing role information.
	 * @returns A promise that resolves when the menu configuration is loaded and filtered.
	 *
	 * @remarks
	 * This method fetches the menu configuration from a `menu_config.json` file located in the same directory.
	 * It then filters the menu and submenu items to include only those that match the roles of the provided user.
	 *
	 * @throws Will throw an error if the fetch operation fails or if the JSON parsing fails.
	 */
	public async loadMenuConfig(user: User): Promise<void> {
		// Load the menu configuration from ./menu_config.json
		const response = await fetch(sap.ui.require.toUrl(Menu.MENU_CONFIG_URL));
		if (!response.ok) {
			throw new Error(`Failed to load menu configuration: ${response.statusText}`);
		}
		this.menuConfig = (await response.json()) as { menu: Array<MenuItem> };
		// Only get the menu and submenu items that match the users roles
		this.menuConfig = this.filterMenuByScopes(
			this.menuConfig as { menu: Array<MenuItem> },
			user.getScopes()
		);
	}
	/**
	 * Gets the menu configuration.
	 * @returns The menu configuration object.
	 */
	public getMenuConfig(): { menu: Array<MenuItem> } {
		return this.menuConfig;
	}

	/**
	 * Filters the menu items based on the user's roles.
	 * @param menu - The menu object to filter.
	 * @param scopes - The roles of the user.
	 * @returns The filtered menu object.
	 */
	private filterMenuByScopes(
		menu: { menu: Array<MenuItem> },
		scopes: string[]
	): { menu: Array<MenuItem> } {
		const filteredMenu = { menu: [] as Array<MenuItem> };
		for (const item of menu["menu"]) {
			// Filter the submenus
			if (this.itemIsAccessible(item, scopes)) {
				filteredMenu.menu.push(this.filterSubmenuByScopes(item, scopes));
			}
		}
		return filteredMenu;
	}

	/**
	 * Filters the submenu items of a menu item based on the user's roles.
	 * @param menuItem - The menu item to filter.
	 * @param scopes - The roles of the user.
	 * @returns The filtered menu item.
	 */
	private filterSubmenuByScopes(
		menuItem: MenuItem,
		scopes: string[]
	): MenuItem {
		if (!("submenu" in menuItem)) {
			return menuItem;
		}

		const submenus = menuItem["submenu"];
		menuItem["submenu"] = submenus.filter((submenu) =>
			this.itemIsAccessible(submenu, scopes)
		);

		return menuItem;
	}

	/**
	 * Checks if a menu item is accessible based on the user's roles.
	 * @param menuItem - The menu item to check.
	 * @param scopes - The roles of the user.
	 * @returns True if the menu item is accessible, false otherwise.
	 */
	private itemIsAccessible(
		menuItem: MenuItem,
		scopes: string[]
	): boolean {
		if (!menuItem.scopes) return false;
		const itemScopes = menuItem.scopes;
		
		// Check if any of the item's scopes match the user's scopes (including wildcards)
		return itemScopes.some((scopePattern) => this.matchesScope(scopePattern, scopes));
	}

	/**
	 * Checks if a scope pattern matches any of the user's scopes.
	 * All patterns are treated as regex patterns
	 * @param scopePattern - The regex pattern to match
	 * @param userScopes - The user's actual scopes
	 * @returns True if the pattern matches at least one user scope
	 */
	private matchesScope(scopePattern: string, userScopes: string[]): boolean {
		try {
			const regex = new RegExp(`^${scopePattern}$`);
			
			// Check if any user scope matches the pattern
			return userScopes.some(userScope => regex.test(userScope));
		} catch (error) {
			console.warn(`Invalid regex pattern: ${scopePattern}`, error);
			return false;
		}
	}

	attachMenuChange(callback: (event?: Event & { mParameters: { menuConfig: { menu: Array<MenuItem> } } }) => void | Promise<void>, listener?: object): Menu {
		// Attach the callback to the menu change event
		this.attachEvent("menuChange", callback, listener);
		return this;
	}

	fireMenuChange(): void {
		this.fireEvent("menuChange", {
			menuConfig: this.getMenuConfig()
		});
	}

}

export type MenuItem = {
	name: string;
	scopes: string[];
	target?: string;
	parameters?: string;
	submenu?: Array<MenuItem>;
	icon?: string;
	badge?: { amount: number; class: string, data: any };
}
